import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { HelianthusArbiter } from '@amzn/helianthus-arbiter-client';
import { createMidwayCredentialProvider } from '../api/auth';
import { getSettings } from '../api/settings';
import { Spinner, Box } from '@cloudscape-design/components';

interface ArbiterClientContextValue {
  client: HelianthusArbiter;
}

const ArbiterClientContext = createContext<ArbiterClientContextValue | null>(null);

interface ArbiterClientProviderProps {
  children: ReactNode;
}

export function ArbiterClientProvider({ children }: ArbiterClientProviderProps): ReactNode {
  const [client, setClient] = useState<HelianthusArbiter | null>(null);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function init(): Promise<void> {
      const settings = await getSettings();
      const arbiterClient = new HelianthusArbiter({
        region: settings.region,
        endpoint: settings.apiEndpoint,
        credentials: createMidwayCredentialProvider(),
      });
      if (!cancelled) {
        setClient(arbiterClient);
      }
    }

    init().catch((err) => {
      if (!cancelled) {
        setError(err instanceof Error ? err : new Error(String(err)));
      }
    });

    return () => { cancelled = true; };
  }, []);

  if (error) {
    return (
      <Box textAlign="center" padding={{ top: 'xxxl' }}>
        <Box variant="h2" color="text-status-error">Failed to initialize client</Box>
        <Box variant="p" color="text-body-secondary">{error.message}</Box>
      </Box>
    );
  }

  if (!client) {
    return (
      <Box textAlign="center" padding={{ top: 'xxxl' }}>
        <Spinner size="large" />
        <Box variant="p" padding={{ top: 's' }}>Initializing...</Box>
      </Box>
    );
  }

  return (
    <ArbiterClientContext.Provider value={{ client }}>
      {children}
    </ArbiterClientContext.Provider>
  );
}

export function useArbiterClient(): HelianthusArbiter {
  const ctx = useContext(ArbiterClientContext);
  if (!ctx) {
    throw new Error('useArbiterClient must be used within ArbiterClientProvider');
  }
  return ctx.client;
}
